import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isExpanded } from 'bpmn-js/lib/util/DiUtil';

/**
 * Assigns default background colours to newly created elements:
 *   • Task (all sub-types)        → Orange
 *   • DataObjectReference         → Yellow
 *   • Collapsed Sub-Process       → Blue  (also on replace / toggle-collapse)
 *
 * Colors are applied inside `commandStack.postExecuted` so they form
 * a single undo-step together with the originating command.
 */
export default function DefaultColorProvider(eventBus, modeling, elementRegistry, commandStack) {
  const DEFAULTS = [
    { check: 'bpmn:Task',                fill: '#FFE0B2', stroke: '#6B3C00' },
    { check: 'bpmn:DataObjectReference',  fill: '#FFF9C4', stroke: '#6D5E00' },
  ];

  const SUB_PROCESS_COLOR = { fill: '#BBDEFB', stroke: '#0D4372' };

  // --- New elements (drag from palette, paste, import) ---
  eventBus.on('commandStack.shape.create.postExecuted', (event) => {
    const { shape } = event.context;
    if (!shape || !hasNoColor(shape)) return;

    // Collapsed sub-process
    if (is(shape, 'bpmn:SubProcess') && !isExpanded(shape)) {
      modeling.setColor(shape, SUB_PROCESS_COLOR);
      return;
    }

    for (const def of DEFAULTS) {
      if (is(shape, def.check)) {
        modeling.setColor(shape, { fill: def.fill, stroke: def.stroke });
        break;
      }
    }
  });

  // --- Replace (e.g. Task → collapsed Sub-Process) ---
  eventBus.on('commandStack.shape.replace.postExecuted', (event) => {
    const { newShape } = event.context;
    if (!newShape) return;
    if (is(newShape, 'bpmn:SubProcess') && !isExpanded(newShape)) {
      modeling.setColor(newShape, SUB_PROCESS_COLOR);
    }
  });

  // --- Toggle collapse (expanded ↔ collapsed) ---
  eventBus.on('commandStack.shape.toggleCollapse.postExecuted', (event) => {
    const { shape } = event.context;
    if (!shape) return;
    if (is(shape, 'bpmn:SubProcess') && !isExpanded(shape)) {
      modeling.setColor(shape, SUB_PROCESS_COLOR);
    }
  });

  // --- Auto-colour on file open (when no element has colours yet) ---
  eventBus.on('import.render.complete', () => {
    const elements = elementRegistry.getAll();

    // If any element already carries a colour the file was intentionally styled
    if (elements.some(el => el.di && !hasNoColor(el))) return;

    const tasks = [];
    const dataObjects = [];
    const collapsedSubProcesses = [];

    for (const el of elements) {
      if (!el.di || el.labelTarget) continue;
      if (is(el, 'bpmn:SubProcess') && !isExpanded(el)) {
        collapsedSubProcesses.push(el);
      } else if (is(el, 'bpmn:Task')) {
        tasks.push(el);
      } else if (is(el, 'bpmn:DataObjectReference')) {
        dataObjects.push(el);
      }
    }

    let colored = false;
    if (tasks.length > 0) {
      modeling.setColor(tasks, { fill: DEFAULTS[0].fill, stroke: DEFAULTS[0].stroke });
      colored = true;
    }
    if (dataObjects.length > 0) {
      modeling.setColor(dataObjects, { fill: DEFAULTS[1].fill, stroke: DEFAULTS[1].stroke });
      colored = true;
    }
    if (collapsedSubProcesses.length > 0) {
      modeling.setColor(collapsedSubProcesses, SUB_PROCESS_COLOR);
      colored = true;
    }

    // Clear command stack so auto-colouring is not recorded as a user change
    if (colored) {
      commandStack.clear();
    }
  });
}

DefaultColorProvider.$inject = ['eventBus', 'modeling', 'elementRegistry', 'commandStack'];

/** True when the element has no colour assigned yet. */
function hasNoColor(shape) {
  const di = shape.di;
  return !(di?.get?.('background-color') || di?.get?.('bioc:fill'));
}
